import ReactGA from 'react-ga4';
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { BillingIntervalEnum } from 'enums';
import BaseModal from 'components/BaseModal';
import { makeStyles } from '@mui/styles';
import { Box, Button, Grid, Typography } from '@mui/material';
import { COLOR_GRAY_70, COLOR_GRAY_90, COLOR_PINK, COLOR_PURPLE } from 'styles/variables/colors';
import CustomButton from 'components/CustomButton';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useAuthUser } from 'features/auth/AuthUserContext';
import useGeoData from 'hooks/useGeoData';
import { getMtgPricesFromEditage } from 'utils/plan';
import { getEnv, getMtgContinueUrl } from 'utils';
import * as queryString from 'query-string';
import DiscountBanner from 'components/DiscountBanner';
import { geoDiscountConfig } from 'config/discount';
import { useGoogleAnalytics } from 'hooks/useGoogleAnalytics';
import getCleverTap from 'utils/clevertap';

export const useStyles = makeStyles((theme) => ({
  rootModal: {
    paddingBottom: theme.spacing(3),
  },
  description: {
    color: COLOR_GRAY_90,
    lineHeight: '24px',
    textAlign: 'center',

    '& span': {
      fontWeight: 900,
    },
  },
  mainButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    maxWidth: 320,
    gap: 8,
    borderRadius: 8,
    border: '1px solid #EAECF0',
    backgroundColor: '#F9FAFB',
  },
  button: {
    padding: theme.spacing(1, 2),
    borderRadius: 6,
    fontWeight: 900,
    color: COLOR_GRAY_70,
    textTransform: 'none',
    textAlign: 'center',
    border: '1px solid transparent',

    '&:hover': {
      backgroundColor: theme.palette.common.white,
      boxShadow: '0px 1px 3px 0px rgba(16, 24, 40, 0.10)',
    },
  },
  buttonSelected: {
    color: COLOR_PURPLE,
    backgroundColor: theme.palette.common.white,
    border: '1px solid #EAECF0',
  },
  boxPrice: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    borderRadius: 16,
    border: `1px solid ${COLOR_PINK}`,
    boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
  },
  researcherText: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '22px',
    color: COLOR_PINK,
  },
  mostPopular: {
    backgroundColor: '#FDF2FA',
    border: '1px solid #FCCEEE',
    padding: '2px 8px',
    borderRadius: 6,
    fontWeight: 700,
    fontSize: 14,
    lineHeight: '20px',
    fontFamily: 'Lato',
    color: COLOR_PINK,
  },
  priceValue: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: COLOR_PINK,
    fontSize: 20,
    fontWeight: 700,
    lineHeight: '24px',

    '& span': {
      fontSize: 40,
      fontWeight: 800,
    },

    '& p': {
      fontSize: 18,
      fontWeight: 400,
      color: COLOR_GRAY_70,
      lineHeight: '22px',
    },
  },
  totalPriceYear: {
    fontSize: 11,
    color: COLOR_GRAY_70,
    textAlign: 'left',
    marginTop: -theme.spacing(1.75),
  },
  watermarkFreeText: {
    color: COLOR_GRAY_70,
    textAlign: 'left',
    fontSize: 15,
  },
}));

const Props = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default function LimitationModal({ open, onClose }) {
  const { user, isLoading: isLoadingUser, isAuthenticated } = useAuthUser();
  const classes = useStyles();
  const [billingState, setBillingState] = useState(BillingIntervalEnum.YEAR);
  const [researcherLifeData, setResearcherLifeData] = useState(null);
  const { geoData, isLoadingGeoData } = useGeoData();
  const [discountPercentage, setDiscountPercentage] = useState(null);
  const { gaIdentifiers } = useGoogleAnalytics() || {};
  const clevertap = getCleverTap();

  function sendOpenTrackingEvents() {
    ReactGA.event({
      category: 'workspace',
      action: 'limitation_modal_presented',
      label: 'My Creations Limitation Modal Presented',
      value: user.id,
    });

    clevertap.event.push('Click Workspace', {
      Product: 'MTG',
      Email: user.email,
      'Country code': user.country_code,
      URL: '/workspace/',
      Description: 'Workspace Limitation Modal Presented',
      Locale: 'workspace',
    });
  }

  function getStartedButtonClick(state) {
    let RLIFE_CHECKOUT_URL = getEnv('R_LIFE_BILLING') + '/checkout-subscribe';
    let planMachineName =
      state === BillingIntervalEnum.YEAR
        ? researcherLifeData.annual.pack_machine_name
        : researcherLifeData.monthly.pack_machine_name;

    ReactGA.event({
      category: 'my_creations',
      action: 'limitation_modal_get_started_clicked',
      label: 'My Creations Limitation Modal Get Started Clicked',
      value: user.id,
    });

    clevertap.event.push('Clicks to view information detail', {
      Product: 'MTG',
      Email: user?.email,
      URL: window.location.pathname,
      Description: 'Continue to payment',
      Locale: 'pricing',
      Plan: planMachineName,
    });

    const { client_id, session_id } = gaIdentifiers || {};
    let checkoutUrl = `${RLIFE_CHECKOUT_URL}?${queryString.stringify({
      pack_machine_name: planMachineName,
      usersource: 'mtg',
      utm_source: 'mtg',
      utm_medium: 'limitation-modal',
      ...(client_id && { clientId: client_id }),
      ...(session_id && { sessionId: session_id }),
      continue: getMtgContinueUrl(),
    })}`;

    window.open(checkoutUrl, '_blank');
  }

  function otherPlansClick() {
    ReactGA.event({
      category: 'my_creations',
      action: 'limitation_modal_more_plans_clicked',
      label: "When user click in 'See Other Plans' button",
      value: user.id,
    });

    clevertap.event.push('Click Workspace', {
      Product: 'MTG',
      Email: user.email,
      'Country code': user.country_code,
      URL: '/workspace/',
      Description: 'More Plans Clicked',
      Locale: 'workspace',
    });

    window.open('/pricing', '_blank');
  }

  useEffect(() => {
    let isMounted = true;

    async function fetchResearcherLifeData(country) {
      const data = await getMtgPricesFromEditage((country || 'US').toLowerCase());
      if (isMounted) {
        setResearcherLifeData(data);
      }
    }

    // Only fetch data and track events when the modal actually opens
    if (open && !isLoadingUser && !isLoadingGeoData) {
      // Track event only when modal first opens
      sendOpenTrackingEvents();

      const countryCode = isAuthenticated ? user.country_code : geoData.country_code;
      fetchResearcherLifeData(countryCode);
      setDiscountPercentage((geoDiscountConfig[countryCode] || geoDiscountConfig.US).discount);
    }

    return () => {
      isMounted = false;
    };
  }, [open, isLoadingUser, isLoadingGeoData]);

  // Prevent rendering until data is ready
  if (!researcherLifeData || !open) return null;

  return (
    <BaseModal
      classes={{ paper: classes.rootModal }}
      open={open}
      title=<b>Unlimited creations are just one step away</b>
      size="md"
      fullWidth={false}
      header={''}
      content={
        <Box>
          <Typography className={classes.description}>
            We know you want to create more designs, but you've reached your account's limit. <br />
            <span>Upgrade your subscription and activate unlimited creations</span>at the best
            market's price.
          </Typography>

          <Box display="flex" justifyContent="center" py={4}>
            <Box className={classes.mainButton}>
              <Button
                className={clsx(classes.button, {
                  [classes.buttonSelected]: billingState === 'year',
                })}
                onClick={() => setBillingState(BillingIntervalEnum.YEAR)}
              >
                Yearly
              </Button>

              <Button
                className={clsx(classes.button, {
                  [classes.buttonSelected]: billingState === 'month',
                })}
                onClick={() => setBillingState(BillingIntervalEnum.MONTH)}
              >
                Monthly
              </Button>
            </Box>
          </Box>

          <Grid container={true} spacing={2}>
            <Grid item={true} xs={12} sm={8}>
              <Image
                src="/images/cool-scientist.svg"
                alt="cool scientist image"
                width={555}
                height={339}
                priority={true}
              />
            </Grid>

            <Grid item={true} xs={12} sm={4}>
              <Box className={classes.boxPrice}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Typography className={classes.researcherText}>Researcher</Typography>

                  <Typography className={classes.mostPopular}>Most Popular</Typography>
                </Box>

                <Box py={2}>
                  <div className={classes.priceValue}>
                    <span>
                      {billingState === 'year' &&
                        researcherLifeData &&
                        researcherLifeData.annual.formatted_pack_monthly_amount}
                      {billingState === 'month' && researcherLifeData.monthly.formatted_pack_amount}
                    </span>
                    <p>/month</p>
                  </div>

                  {billingState === 'year' && (
                    <Typography className={classes.totalPriceYear}>
                      {researcherLifeData.annual.formatted_pack_amount} charged yearly{' '}
                    </Typography>
                  )}
                </Box>

                <Typography className={classes.watermarkFreeText}>
                  Watermark-free illustrations + Request customized illustrations
                </Typography>

                <CustomButton
                  style={{ minWidth: '100%', marginTop: 24 }}
                  onClick={() => getStartedButtonClick(billingState)}
                  variant="buttonSecondary"
                >
                  Get started
                </CustomButton>
              </Box>
            </Grid>
          </Grid>

          {/* <Box display="flex" justifyContent="center" pt={4}>
            <CustomButton
              variant="ctaPrimaryOutline"
              onClick={otherPlansClick}
              style={{ minWidth: 344 }}
            >
              See other plans
            </CustomButton>
          </Box> */}
        </Box>
      }
      cancelButton={false}
      actionButton={false}
      onClose={onClose}
    />
  );
}

LimitationModal.propTypes = Props;
